import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Table } from '@/common';
import { Button, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { AuthContext } from '@/contexts';
import { VideoThemeContext } from '@/contexts/VideoThemeProvider';
import { PERMISSION_TYPE } from '@/enums';
import { getVideoThemeTextColumns } from '@/helpers/VideoThemeTextHelper';
import { CreateEditText } from '@/pages/VideoTheme/TextOverlay/CreateEdit';
import { PlusIcons } from '@/resources';
import {
  useDeleteVideoThemeTextMutation,
  useGetTextByVideoThemeIdQuery
} from '@/services/videoThemeText.service';
import { AllowedPermissions, TableData } from '@/types';
import { VideoThemeText } from '@/types/VideoThemeTypes';

type TextOverlayProps = {
  id: string;
};

export const TextOverlay = ({ id }: TextOverlayProps) => {
  const [t] = useTranslation('common');
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const {
    data: videoThemeText,
    isLoading: isLoadingText,
    refetch
  } = useGetTextByVideoThemeIdQuery(Number(id));
  const [selectedText, setSelectedText] = useState<VideoThemeText | undefined>();
  const [isAddNewTextModalOpen, setIsAddNewTextModalOpen] = useState<boolean>(false);
  const [isEditTextModalOpen, setIsEditTextModalOpen] = useState<boolean>(false);
  const { refetch: refetchTheme } = useContext(VideoThemeContext);

  const allowedPermissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_VIDEO_THEME,
    editPermission: PERMISSION_TYPE.UPDATE_VIDEO_THEME,
    deletePermission: PERMISSION_TYPE.DELETE_VIDEO_THEME
  };

  const { permissions } = useContext(AuthContext);
  const editVideoThemeAccess = permissions.includes(PERMISSION_TYPE.UPDATE_VIDEO_THEME);
  const createVideoThemeAccess = permissions.includes(PERMISSION_TYPE.CREATE_VIDEO_THEME);

  const handleDeleteClip = (row: VideoThemeText) => {
    setIsOpenDeleteModal(true);
    setSelectedText(row);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedText(undefined);
  };

  const { mutate: deleteClip } = useDeleteVideoThemeTextMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      refetch();
      refetchTheme();
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const handleEdit = (videoThemeTextData: VideoThemeText) => {
    if (editVideoThemeAccess) {
      setIsEditTextModalOpen(true);
      setSelectedText(videoThemeTextData);
    }
  };

  const formatData = (): TableData[] => {
    return videoThemeText?.data.map((item: VideoThemeText) => ({
      id: item.id,
      value: item.value,
      fontFamily: item.fontFamily,
      fontSize: item.fontSize,
      textColor: item.textColor,
      alignment: item.alignment,
      startTime: item.startTime,
      endTime: item.endTime,
      marginTop: item.marginTop,
      marginRight: item.marginRight,
      marginBottom: item.marginBottom,
      marginLeft: item.marginLeft,
      timeFromStart: item.timeFromStart,
      transitionTime: item.transitionTime,
      enabled: true
    })) as TableData[];
  };

  return (
    <div className='mt-5 w-full overflow-auto md:mt-7 md:px-10'>
      {isLoadingText ? (
        <Spinner />
      ) : (
        <div>
          {videoThemeText && (
            <Table
              data={formatData()}
              columns={getVideoThemeTextColumns()}
              permissions={allowedPermissions}
              handleEdit={(item) => handleEdit(item as VideoThemeText)}
              handleDelete={(item) => handleDeleteClip(item as VideoThemeText)}
              handleViewDetails={(item) => handleEdit(item as VideoThemeText)}
              fetchNextPage={() => {
                throw new Error('Function not implemented.');
              }}
              hasNextPage={undefined}
              isFetchingNextPage={false}
              numberOfAppliedFilters={0}
              hiddenColumnsMobile={['enabled']}
              mobileColumnOffset={3}
            />
          )}
          {selectedText && (
            <ConfirmationModal
              isOpen={isOpenDeleteModal}
              message={t('deleteMessage', {
                item: `${selectedText.value}`
              })}
              onClose={handleCloseDeleteModal}
              onConfirm={() => {
                selectedText && selectedText.id && deleteClip(selectedText.id);
              }}
            />
          )}
          {createVideoThemeAccess && (
            <div className='flex w-full flex-col justify-between'>
              <Button
                className='mx-[35px] mb-4 mt-5'
                variant={ButtonVariant.QUINARY}
                handleOnClick={() => {
                  setIsAddNewTextModalOpen(true);
                }}>
                <PlusIcons className='size-3.5 md:mr-3' fill='var(--color-primary-blue)' />
                {t('addNewText')}
              </Button>
            </div>
          )}
          {isAddNewTextModalOpen && (
            <CreateEditText
              isOpen={isAddNewTextModalOpen}
              onClose={() => {
                setIsAddNewTextModalOpen(false);
                refetch();
                refetchTheme();
              }}
              isCreate={true}
              videoThemeId={Number(id)}
            />
          )}
          {isEditTextModalOpen && (
            <CreateEditText
              isOpen={isEditTextModalOpen}
              isCreate={false}
              videoThemeText={selectedText}
              onClose={() => {
                setIsEditTextModalOpen(false);
                setSelectedText(undefined);
                refetch();
                refetchTheme();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
